import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, DataQualityResponse, DataQualityRun } from "../../lib/api";
import { Play } from "lucide-react";

export function DataQualityPage() {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["data-quality"],
    queryFn: () => api.get<DataQualityResponse>("/api/v1/admin/data-quality"),
    refetchInterval: 30_000,
  });

  const trigger = useMutation({
    mutationFn: () => api.post<DataQualityRun>("/api/v1/admin/data-quality/run"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-quality"] });
      refetch();
    },
  });

  return (
    <div style={{ padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, color: "#1a365d", margin: 0 }}>Якість даних</h1>
        <button
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending}
          style={btnPrimary}
        >
          <Play size={13} /> {trigger.isPending ? "Запуск…" : "Запустити перевірку"}
        </button>
      </div>

      {isLoading && <div>Завантаження…</div>}

      {data && (
        <>
          <div style={{ background: "white", padding: 16, borderRadius: 6, border: "1px solid #e2e8f0", marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: "#718096" }}>
              Усього прогонів: <strong>{data.history_count}</strong>
            </div>
            {data.last_run ? (
              <div style={{ fontSize: 13, color: "#718096", marginTop: 4 }}>
                Останній: <strong>{new Date(data.last_run.started_at).toLocaleString("uk-UA")}</strong> ·
                Статус: <span style={statusBadge(data.last_run.status)}>{data.last_run.status}</span> ·
                Запуск: {data.last_run.triggered_by}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#a0aec0" }}>Жодного прогону ще не було.</div>
            )}
          </div>

          {data.last_run && (
            <div style={{ background: "white", borderRadius: 6, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#ebf4ff", textAlign: "left", color: "#2c5282" }}>
                    <th style={th}>Source</th>
                    <th style={th}>Severity</th>
                    <th style={th}>Field</th>
                    <th style={th}>Expected</th>
                    <th style={th}>Actual</th>
                    <th style={th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.last_run.findings.map((f) => (
                    <tr key={f.finding_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={td}><code>{f.source}</code></td>
                      <td style={td}><span style={severityBadge(f.severity)}>{f.severity}</span></td>
                      <td style={td}>{f.field_name || "—"}</td>
                      <td style={td}>{f.expected || "—"}</td>
                      <td style={td}>{f.actual || "—"}</td>
                      <td style={td}>{f.action_taken || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function statusBadge(status: string): React.CSSProperties {
  const colors: Record<string, string> = {
    success: "#48bb78", running: "#4299e1", partial: "#ed8936", failed: "#e53e3e",
  };
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: colors[status] || "#4a5568", background: (colors[status] || "#a0aec0") + "20",
  };
}

function severityBadge(s: string): React.CSSProperties {
  const colors: Record<string, string> = { critical: "#9b2c2c", high: "#e53e3e", medium: "#ed8936", low: "#48bb78" };
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: colors[s] || "#4a5568", background: (colors[s] || "#a0aec0") + "20",
  };
}

const th: React.CSSProperties = { padding: "8px 12px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 12px", verticalAlign: "top" };
const btnPrimary: React.CSSProperties = {
  padding: "7px 14px", background: "#2c5282", color: "white", border: "none",
  borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4,
};
